import { useEffect } from 'react';
import { Button, Popper, PopperProps, Slide, Fade, Paper, Theme, Alert, Box } from '@mui/material';
import { DocumentTypes } from 'types';
import GVTooltip from 'components/lib/GVToolTip/GVTooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { GVTypography } from 'components/lib';
import GVTextButton from 'components/lib/GVTextButton/GVTextButton';
import { getStyleVariables } from 'styles/vars';
import { makeStyles } from 'tss-react/mui';
import { useDocumentToastStateStore } from 'zstore/documentToastStateStore';
import SuccessCheckmark from 'components/icons/FontMetrics/SuccessCheckmark';
import TriangleWarning from 'components/icons/FontMetrics/TriangleWarning';

export interface DocumentToast {
  message: string;
  type: 'error' | 'success' | 'info' | 'warning' | 'shifted' | 'fontMetricsPass' | 'fontMetricsFail' | 'escToExit';
  duration?: number;
  expired?: boolean;
  // for actions after dismissing the toast (ex. update auth0 preferences)
  onDismiss?: () => void;
  infoMessage?: string;
  actionButtonTitle?: string;
  onActionButton?: () => void;
}

const useManualAlignStyles = makeStyles()((theme: Theme) => ({
  paper: {
    display: 'flex',
    width: 366,
    minHeight: 40,
    padding: '4px 16px 4px 16px',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(3),
    background: 'var(--gds-color-cool-gray-900, #101113)',
    borderRadius: '4px',
    boxShadow: theme.shadows[6],
    '& .manualAlignText': {
      fontSize: 12,
      fontWeight: 600,
      lineHeight: '16px',
    },
  },
}));

const useShiftedStyles = makeStyles()((theme: Theme) => ({
  paper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 1.5),
    marginTop: theme.spacing(3),
    backgroundColor: 'white',
    color: 'black',
    fontSize: '11px',
  },
  tooltip: {
    marginLeft: theme.spacing(2),
  },
  actionButton: {
    marginLeft: theme.spacing(2),
  },
}));

const useFontMetricsStyles = makeStyles()((theme: Theme) => ({
  paperSuccess: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 1.5),
    marginTop: theme.spacing(3),
    backgroundColor: theme.palette.success.main,
    color: 'white',
    borderRadius: '4px',
    maxWidth: theme.spacing(58),
  },
  paperError: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 1.5),
    marginTop: theme.spacing(3),
    backgroundColor: theme.palette.error.main,
    color: 'white',
    borderRadius: '4px',
    maxWidth: theme.spacing(58),
  },
  dismissButton: {
    color: 'white',
    marginLeft: theme.spacing(2),
  },
  contentWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flex: 1,
  },
  icon: {
    fontSize: '24px',
  },
  messageText: {
    flex: 1,
  },
}));

const useAlertStyles = makeStyles()((theme: Theme) => {
  const styleVariables = getStyleVariables(theme);
  return {
    root: {
      maxWidth: theme.spacing(58),
      marginTop: theme.spacing(3),
      backgroundColor: styleVariables.global.darkBorder,
      alignItems: 'center',
      '& .MuiAlert-action': {
        alignItems: 'center',
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
    rootWarning: {
      maxWidth: theme.spacing(58),
      marginTop: theme.spacing(3),
      backgroundColor: '#FFF3CD',
      color: '#856404',
      display: 'flex',
      alignItems: 'center', // Vertically center all content
      '& .MuiAlert-icon': {
        color: '#FF8C00', // Orange icon
        display: 'flex',
        alignItems: 'center',
      },
      '& .MuiAlert-action': {
        display: 'flex',
        alignItems: 'center', // Vertically center the action area
        paddingLeft: 0,
        paddingTop: 0,
        paddingBottom: 0,
      },
      '& .MuiButton-root': {
        color: '#FF8C00', // Orange button text
        minHeight: 'auto',
        padding: theme.spacing(0.5, 1),
      },
    },
    message: {
      padding: theme.spacing(0.5, 0),
    },
  };
});

type DocumentToastProps = Pick<PopperProps, 'anchorEl'> & {
  documentType: DocumentTypes;
};

export const DocumentToast = ({ anchorEl, documentType }: DocumentToastProps) => {
  const { classes: alertClasses } = useAlertStyles();
  const { classes: shiftedClasses } = useShiftedStyles();
  const { classes: manualAlignClasses } = useManualAlignStyles();
  const { classes: fontMetricsClasses } = useFontMetricsStyles();
  const { documentToast: documentToasts, expireDocumentToast } = useDocumentToastStateStore();

  const documentToast = documentToasts[documentType];
  const { message, type, duration = null, expired = false, infoMessage } = documentToast || {};

  useEffect(() => {
    const expireTimeout = setTimeout(() => {
      if (duration && documentToast && !documentToast.expired) {
        // set the current document toast to expired after duration
        expireDocumentToast(documentType, documentToast);
      }
    }, duration || 0);
    return () => clearTimeout(expireTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentToast]);

  const handleDismiss = () => {
    if (documentToast) {
      // expire current toast on dismiss
      expireDocumentToast(documentType, documentToast);
    }
    if (documentToast?.onDismiss) {
      documentToast.onDismiss();
    }
  };

  if (!documentToast) {
    return null;
  }

  const handleActionButton = () => {
    if (typeof documentToast?.onActionButton !== 'undefined') {
      documentToast?.onActionButton();
      // expire current toast on cancel
      expireDocumentToast(documentType, documentToast);
    }
  };

  let alert: JSX.Element;
  if (type === 'escToExit') {
    alert = (
      <Paper elevation={6} variant="elevation" className={manualAlignClasses.paper}>
        <GVTypography className="manualAlignText">{message}</GVTypography>
        {infoMessage && <GVTypography className="manualAlignText">{infoMessage}</GVTypography>}
      </Paper>
    );
  } else if (type === 'shifted') {
    alert = (
      <Paper elevation={6} variant="elevation" className={shiftedClasses.paper}>
        <GVTypography>{message}</GVTypography>
        {infoMessage && (
          <GVTooltip
            className={shiftedClasses.tooltip}
            title={<GVTypography variant="inherit">{infoMessage}</GVTypography>}
          >
            <InfoOutlinedIcon color="action" />
          </GVTooltip>
        )}
        {documentToast.actionButtonTitle && (
          <GVTextButton
            text={documentToast.actionButtonTitle}
            className={shiftedClasses.actionButton}
            variant="contained"
            color="secondary"
            id="actionButton"
            onClick={handleActionButton}
          />
        )}
      </Paper>
    );
  } else if (type === 'fontMetricsPass') {
    alert = (
      <Paper elevation={6} variant="elevation" className={fontMetricsClasses.paperSuccess}>
        <Box className={fontMetricsClasses.contentWrapper}>
          <SuccessCheckmark className={fontMetricsClasses.icon} />
          <GVTypography color="inherit" className={fontMetricsClasses.messageText}>
            {message}
          </GVTypography>
        </Box>
        <Button onClick={handleDismiss} className={fontMetricsClasses.dismissButton} size="small">
          DISMISS
        </Button>
      </Paper>
    );
  } else if (type === 'fontMetricsFail') {
    alert = (
      <Paper elevation={6} variant="elevation" className={fontMetricsClasses.paperError}>
        <Box className={fontMetricsClasses.contentWrapper}>
          <TriangleWarning className={fontMetricsClasses.icon} />
          <GVTypography color="inherit" className={fontMetricsClasses.messageText}>
            {message}
          </GVTypography>
        </Box>
        <Button onClick={handleDismiss} className={fontMetricsClasses.dismissButton} size="small">
          DISMISS
        </Button>
      </Paper>
    );
  } else {
    // Use different classes based on type to control background override
    const alertClassesToUse =
      type === 'warning' ? { root: alertClasses.rootWarning, message: alertClasses.message } : alertClasses;

    alert = (
      <Alert
        action={
          <Button onClick={handleDismiss} color="inherit" size="small">
            DISMISS
          </Button>
        }
        elevation={6}
        variant="filled"
        color={type}
        severity={type}
        classes={alertClassesToUse}
      >
        {message}
      </Alert>
    );
  }

  return (
    <Popper open={!!anchorEl && !expired} anchorEl={anchorEl} placement="bottom" transition>
      {({ TransitionProps }) => (
        <Slide {...TransitionProps} direction="down" timeout={700}>
          <div>
            <Fade {...TransitionProps} timeout={250}>
              {alert}
            </Fade>
          </div>
        </Slide>
      )}
    </Popper>
  );
};
